import { ChevronsUpDown } from 'lucide-react';
import type { Page } from '../../../types';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    ArrowLeftIcon,
    CreditCardIcon,
    HandshakeIcon,
    LifeBuoyIcon,
    LogoutIcon,
    MoreHorizontalIcon,
    ShieldCheckIcon,
    UserIcon,
} from '../../icons';
import { getSlugFromPage } from '../../../utils/routeUtils';
import { useIsSuperadmin } from '../../../features/superadmin/hooks/useIsSuperadmin';
import { PROFILE_ROUTE_PAGES, SIDEBAR_ACCOUNT_MENU_ITEM_CLASS } from './config';
import { handleSidebarNavClick } from './navHandlers';
import { SidebarAccountAvatar } from './SidebarAccountAvatar';
import {
    cnMobileBottomNavIcon,
    cnMobileBottomNavItem,
    cnMobileBottomNavLabel,
} from '../layoutNavTokens';

interface SidebarAccountSectionProps {
    isOpen: boolean;
    isBlocked: boolean;
    currentPage: Page;
    setCurrentPage: (page: Page) => void;
    logout: () => void | Promise<void>;
    canViewBilling: boolean;
    canViewSupport: boolean;
    hasPartnerAccess?: boolean;
    clientProfileName?: string | null;
    displayName: string;
    userEmail?: string | null;
    userFullName?: string | null;
    initials: string;
    avatarUrl?: string | null;
    avatarError: boolean;
    setAvatarError: (v: boolean) => void;
    avatarLoaded: boolean;
    setAvatarLoaded: (v: boolean) => void;
    /** CRM / Superadmin / Partner shell menus */
    variant?: 'crm' | 'superadmin' | 'partner';
    onNavigate?: () => void;
}

const ACCOUNT_LINKS = [
    { page: 'profile' as const, label: 'Perfil', Icon: UserIcon },
    { page: 'billing' as const, label: 'Faturamento', Icon: CreditCardIcon, needs: 'billing' as const },
    { page: 'support' as const, label: 'Suporte', Icon: LifeBuoyIcon, needs: 'support' as const },
];

const MOBILE_SETTINGS_PAGE: Page = 'settings';
const MENU_ICON = 'h-4 w-4 text-text-muted';
const TRIGGER_ACCENT =
    'transition-colors group-hover:text-primary group-data-[state=open]:text-primary group-data-[active]:text-primary';

export function SidebarAccountSection({
    isOpen,
    isBlocked,
    currentPage,
    setCurrentPage,
    logout,
    canViewBilling,
    canViewSupport,
    hasPartnerAccess = false,
    clientProfileName,
    displayName,
    userEmail,
    userFullName,
    initials,
    avatarUrl,
    avatarError,
    setAvatarError,
    avatarLoaded,
    setAvatarLoaded,
    variant = 'crm',
    onNavigate,
}: SidebarAccountSectionProps) {
    const { isSuperadmin } = useIsSuperadmin();
    const isSuperadminShell = variant === 'superadmin';
    const isPartnerShell = variant === 'partner';
    const isActive = !isSuperadminShell && !isPartnerShell && PROFILE_ROUTE_PAGES.includes(currentPage);
    const avatarProps = {
        initials,
        avatarUrl,
        avatarError,
        onAvatarError: () => setAvatarError(true),
    };

    const goTo = (page: Page) => (e: React.MouseEvent<HTMLAnchorElement>) => {
        handleSidebarNavClick(e, setCurrentPage, page);
        onNavigate?.();
    };

    const menuLinks = isSuperadminShell
        ? [{ page: 'dashboard' as const, label: 'Voltar ao CRM', Icon: ArrowLeftIcon }]
        : isPartnerShell
          ? [{ page: 'dashboard' as const, label: 'Voltar ao CRM', Icon: ArrowLeftIcon }]
          : [
              ...ACCOUNT_LINKS.filter((l) => {
                  if (isBlocked) return false;
                  if (!l.needs) return true;
                  return l.needs === 'billing' ? canViewBilling : canViewSupport;
              }),
              ...(!isBlocked && hasPartnerAccess
                  ? [{ page: 'parceiro' as const, label: 'Portal Parceiro', Icon: HandshakeIcon }]
                  : []),
              ...(!isBlocked && isSuperadmin
                  ? [{ page: 'superadmin' as const, label: 'Superadmin', Icon: ShieldCheckIcon }]
                  : []),
          ];

    return (
        <div
            className={cn(
                'flex w-full min-w-0 justify-center md:mt-auto md:w-full md:items-center',
                isSuperadminShell ? 'px-0' : isOpen ? 'md:px-s4' : 'md:px-s2',
            )}
        >
            {!isSuperadminShell && !isPartnerShell ? (
                <a
                    href={`/${getSlugFromPage(MOBILE_SETTINGS_PAGE)}`}
                    aria-label="Configurações"
                    aria-current={isActive ? 'page' : undefined}
                    data-active={isActive || undefined}
                    onClick={(e) => handleSidebarNavClick(e, setCurrentPage, MOBILE_SETTINGS_PAGE)}
                    className={cn(cnMobileBottomNavItem(), 'md:hidden')}
                >
                    <MoreHorizontalIcon className={cnMobileBottomNavIcon(isActive)} aria-hidden />
                    <span className={cnMobileBottomNavLabel(isActive)}>Mais</span>
                </a>
            ) : null}

            <div className={cn('w-full min-w-0', !isSuperadminShell && !isPartnerShell && 'hidden md:block')}>
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            title={!isOpen ? 'Menu do Usuário' : undefined}
                            aria-label="Menu da conta"
                            aria-haspopup="menu"
                            data-active={isActive || undefined}
                            className={cn(
                                'group relative flex w-full min-w-0 touch-manipulation items-center rounded-lg text-text-secondary transition-colors duration-200',
                                'hover:bg-primary-subtle hover:text-primary',
                                'data-[state=open]:bg-primary/10 data-[state=open]:text-primary',
                                'data-[active]:bg-primary/10 data-[active]:text-primary',
                                isSuperadminShell
                                    ? 'min-h-11 gap-s3 px-s3 py-s2'
                                    : cn('p-s2', isOpen ? 'gap-s3' : 'justify-center'),
                            )}
                        >
                            <SidebarAccountAvatar {...avatarProps} />
                            {isOpen ? (
                                <>
                                    <span className="grid min-w-0 flex-1 gap-0.5 overflow-hidden text-left leading-snug">
                                        <span className={cn('truncate text-sm font-semibold text-default', TRIGGER_ACCENT)}>
                                            {displayName}
                                        </span>
                                        {userEmail ? (
                                            <span
                                                className={cn(
                                                    'truncate text-xs text-muted',
                                                    'transition-colors group-hover:text-primary/80 group-data-[state=open]:text-primary/80 group-data-[active]:text-primary/80',
                                                )}
                                            >
                                                {userEmail}
                                            </span>
                                        ) : null}
                                    </span>
                                    <ChevronsUpDown
                                        className={cn('ml-auto h-4 w-4 shrink-0 text-muted', TRIGGER_ACCENT)}
                                        aria-hidden
                                    />
                                </>
                            ) : (
                                <span
                                    role="tooltip"
                                    className="pointer-events-none absolute left-14 z-dropdown hidden whitespace-nowrap rounded border border-border bg-surface px-s2 py-1 text-xs text-default opacity-0 shadow-md transition-opacity group-hover:opacity-100 md:block"
                                >
                                    {clientProfileName || userFullName || 'Minha Conta'}
                                </span>
                            )}
                        </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                        side="top"
                        align="end"
                        sideOffset={8}
                        collisionPadding={12}
                        className="z-dropdown w-[min(100vw-2rem,16rem)] min-w-[14rem] rounded-xl border border-border bg-surface p-0 text-default shadow-md"
                    >
                        <header className="flex items-center gap-s3 border-b border-border px-s4 py-s3">
                            <SidebarAccountAvatar
                                {...avatarProps}
                                imageLoaded={avatarLoaded}
                                onImageLoad={() => setAvatarLoaded(true)}
                            />
                            <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
                                <p className="truncate text-sm font-semibold leading-snug text-default">{displayName}</p>
                                {userEmail ? (
                                    <p className="truncate text-xs leading-snug text-muted">{userEmail}</p>
                                ) : null}
                            </div>
                        </header>

                        <nav aria-label="Conta" className="p-1">
                            {menuLinks.map(({ page, label, Icon }) => (
                                <DropdownMenuItem key={page} asChild className={SIDEBAR_ACCOUNT_MENU_ITEM_CLASS}>
                                    <a href={`/${getSlugFromPage(page)}`} onClick={goTo(page)}>
                                        <Icon className={MENU_ICON} aria-hidden />
                                        {label}
                                    </a>
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className={cn(
                                    SIDEBAR_ACCOUNT_MENU_ITEM_CLASS,
                                    'text-text-secondary focus:bg-danger-bg focus:text-danger-text data-highlighted:bg-danger-bg data-highlighted:text-danger-text',
                                )}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    void logout();
                                }}
                            >
                                <LogoutIcon className="h-4 w-4" aria-hidden />
                                Sair
                            </DropdownMenuItem>
                        </nav>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
